import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import Holidays from 'date-holidays';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

// En desarrollo el backend corre en localhost:5000; en producción el socket se
// conecta al mismo origen donde se sirve la app.
const SOCKET_BASE = process.env.NODE_ENV === 'production'
  ? window.location.origin
  : 'http://localhost:5000';

const COUNTRY_CODES = {
  'Argentina': 'AR', 'Bahamas': 'BS', 'Barbados': 'BB', 'Belice': 'BZ',
  'Bolivia': 'BO', 'Brasil': 'BR', 'Canadá': 'CA', 'Chile': 'CL',
  'Colombia': 'CO', 'Costa Rica': 'CR', 'Cuba': 'CU', 'Ecuador': 'EC',
  'El Salvador': 'SV', 'Estados Unidos': 'US', 'Guatemala': 'GT', 'Guyana': 'GY',
  'Haití': 'HT', 'Honduras': 'HN', 'Jamaica': 'JM', 'México': 'MX',
  'Nicaragua': 'NI', 'Panamá': 'PA', 'Paraguay': 'PY', 'Perú': 'PE',
  'Puerto Rico': 'PR', 'República Dominicana': 'DO', 'Surinam': 'SR',
  'Trinidad y Tobago': 'TT', 'Uruguay': 'UY', 'Venezuela': 'VE',
};

// Indicativos telefónicos por país (igual que el backend en whatsappContact.js).
const DIAL_CODES = {
  'Argentina': '54', 'Bahamas': '1242', 'Barbados': '1246', 'Belice': '501',
  'Bolivia': '591', 'Brasil': '55', 'Canadá': '1', 'Chile': '56',
  'Colombia': '57', 'Costa Rica': '506', 'Cuba': '53', 'Ecuador': '593',
  'El Salvador': '503', 'Estados Unidos': '1', 'Guatemala': '502', 'Guyana': '592',
  'Haití': '509', 'Honduras': '504', 'Jamaica': '1876', 'México': '52',
  'Nicaragua': '505', 'Panamá': '507', 'Paraguay': '595', 'Perú': '51',
  'Puerto Rico': '1787', 'República Dominicana': '1809', 'Surinam': '597',
  'Trinidad y Tobago': '1868', 'Uruguay': '598', 'Venezuela': '58',
};

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const DAY_HEADERS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const HOURS = Array.from({ length: 15 }, (_, i) => i + 7); // 07:00 – 21:00

const STATUS_C = {
  pendiente: { bg: 'rgba(34,197,94,0.15)', border: '#22c55e', text: '#22c55e' },
  atendida:  { bg: 'rgba(107,114,128,0.15)', border: '#6b7280', text: '#9ca3af' },
  cancelada: { bg: 'rgba(239,68,68,0.12)', border: '#ef4444', text: '#ef4444' },
};

// Opciones al facturar una cita: cada una abre el cajero en el modo elegido
// (igual que en Pedidos).
const FACTURAR_OPTS = [
  { action: 'facturar', label: 'Factura', icon: '🧾' },
  { action: 'facturar_pos', label: 'Factura POS', icon: '🛒' },
  { action: 'cotizar', label: 'Cotización', icon: '📝' },
];

const pad = n => String(n).padStart(2, '0');
// Celular de una empresa que manda plantillas (strictPhone): solo números, sin +,
// espacios ni guiones. El servidor le pone el indicativo del país si no lo trae.
const onlyDigits = (v) => String(v || '').replace(/\D/g, '').slice(0, 15);

// Misma regla que sendsAppointmentTemplates del servidor: premium y confirmación o
// recordatorio de cita activos con su plantilla.
const sendsAppointmentTemplates = (c) => !!c.is_premium
  && ((!!c.confirmacion_activa && !!c.confirmacion_template_id)
    || (!!c.recordatorio_activo && !!c.recordatorio_template_id));
const toDateStr = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const toMonthStr = (y, m) => `${y}-${pad(m + 1)}`;

const inputSt = {
  width: '100%', padding: '7px 10px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '6px', color: theme.colors.text,
  fontSize: '0.85rem', boxSizing: 'border-box', outline: 'none',
};
const labelSt = { display: 'block', color: theme.colors.textMuted, fontSize: '0.82rem', marginBottom: '3px' };
const fieldSt = { marginBottom: '0.8rem' };

function Citas({ token }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const today = new Date();

  const [view, setView] = useState('month');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-indexed
  const [dayNum, setDayNum] = useState(today.getDate());

  const [appointments, setAppointments] = useState([]);
  const [countryCode, setCountryCode] = useState(null);
  const [dialCode, setDialCode] = useState(null); // indicativo telefónico del país de la empresa
  const [holidays, setHolidays] = useState(new Set());
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  // Calendario por usuario: el admin (rol 1,2) puede ver la agenda propia o la de
  // sus trabajadores. El supervisor (rol 3) solo ve la suya (lo fuerza el backend).
  const roleId = Number(localStorage.getItem('role_id')) || null;
  const [perUserCalendar, setPerUserCalendar] = useState(false);
  const [strictPhone, setStrictPhone] = useState(false);
  const cleanPhone = (v) => (strictPhone ? onlyDigits(v) : v);
  const [staff, setStaff] = useState([]);
  const [staffFilter, setStaffFilter] = useState(''); // '' = todos

  const [editAppt, setEditAppt] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ date: '', time: '09:00', name: '', phone: '', service_id: '', duration: '', assigned_user_id: '' });
  const [savingCreate, setSavingCreate] = useState(false);
  const [createError, setCreateError] = useState('');

  // Facturación de una cita: modal para elegir tipo de documento y estado de
  // "preparando" (id de la cita) mientras se traen los ítems antes de ir al cajero.
  const [facturarAppt, setFacturarAppt] = useState(null);
  const [preparing, setPreparing] = useState(null);

  // Buscador de citas (por nombre y/o celular). Solo muestra un listado
  // informativo; el calendario no se mueve. searchResults === null => aún no se
  // ha buscado; [] => se buscó y no hubo coincidencias.
  const [searchName, setSearchName] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Exportar citas a CSV (solo admin y superadmin, rol 1 y 2).
  const isAdmin = roleId === 1 || roleId === 2;
  const [showExport, setShowExport] = useState(false);
  const [exportForm, setExportForm] = useState({ from: '', to: '' });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const authH = { Authorization: `Bearer ${token}` };
  const jsonH = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Load country + services once
  useEffect(() => {
    fetch('/api/pay/company-info', { headers: authH })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        if (d.pais) { setCountryCode(COUNTRY_CODES[d.pais] || null); setDialCode(DIAL_CODES[d.pais] || null); }
        setPerUserCalendar(!!d.per_user_calendar);
        setStrictPhone(sendsAppointmentTemplates(d));
      });
    fetch('/api/services?all=1', { headers: authH })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setServices(d.data || []));
  }, [token]);

  // Lista de usuarios para el selector "Ver citas de:" (solo admin, rol 1 y 2).
  useEffect(() => {
    if (!perUserCalendar || !(roleId === 1 || roleId === 2)) { setStaff([]); return; }
    fetch('/api/appointments/staff', { headers: authH })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setStaff(d.data || []));
  }, [token, perUserCalendar, roleId]);

  // Trabajadores que pueden RECIBIR citas (los deshabilitados en "Configuración
  // horario" siguen saliendo en el filtro de vista, pero no se les puede agendar).
  const bookableStaff = useMemo(() => staff.filter(u => u.cita_enabled), [staff]);

  // Recompute holidays when country or year changes
  useEffect(() => {
    if (!countryCode) { setHolidays(new Set()); return; }
    try {
      const hd = new Holidays(countryCode);
      setHolidays(new Set(
        hd.getHolidays(year).map(h =>
          typeof h.date === 'string' ? h.date.slice(0, 10) : new Date(h.start).toISOString().slice(0, 10)
        )
      ));
    } catch { setHolidays(new Set()); }
  }, [countryCode, year]);

  // Fetch appointments
  const doFetch = (v, y, mo, d) => {
    let q = v === 'month' ? `month=${toMonthStr(y, mo)}` : `date=${toDateStr(y, mo, d)}`;
    // Admin filtrando por un trabajador concreto (calendario por usuario).
    if (perUserCalendar && (roleId === 1 || roleId === 2) && staffFilter) {
      q += `&assigned_user_id=${staffFilter}`;
    }
    setLoading(true);
    return fetch(`/api/appointments?${q}`, { headers: authH })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(data => setAppointments(data.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { doFetch(view, year, month, dayNum); }, [view, year, month, dayNum, token, perUserCalendar, staffFilter]);

  const refetch = () => doFetch(view, year, month, dayNum);

  // ── Buscador ──
  const doSearch = async () => {
    const name = searchName.trim();
    const phone = searchPhone.trim();
    if (!name && !phone) return;
    setSearching(true);
    const q = new URLSearchParams();
    if (name) q.set('name', name);
    if (phone) q.set('phone', phone);
    try {
      const r = await fetch(`/api/appointments/search?${q.toString()}`, { headers: authH });
      const d = r.ok ? await r.json() : { data: [] };
      setSearchResults(d.data || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchResults(null);
    setSearchName('');
    setSearchPhone('');
  };

  // ── Exportar citas a CSV ──
  const openExport = () => {
    // Por defecto, el mes que se está viendo.
    const first = toDateStr(year, month, 1);
    const last = toDateStr(year, month, new Date(year, month + 1, 0).getDate());
    setExportForm({ from: first, to: last });
    setExportError('');
    setShowExport(true);
  };

  const doExport = async () => {
    const { from, to } = exportForm;
    if (!from || !to) return setExportError('Selecciona ambas fechas');
    if (from > to) return setExportError('La fecha inicial no puede ser mayor que la final');
    setExporting(true); setExportError('');
    try {
      const q = new URLSearchParams({ from, to });
      const r = await fetch(`/api/appointments/export?${q.toString()}`, { headers: authH });
      if (!r.ok) {
        let msg = 'No se pudo exportar';
        try { const j = await r.json(); msg = j.message || msg; } catch { /* respuesta no JSON */ }
        setExportError(msg);
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `citas_${from}_a_${to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setShowExport(false);
    } catch {
      setExportError('No se pudo exportar');
    } finally {
      setExporting(false);
    }
  };

  // "2026-07-04 09:00:00" -> { day: '2026-07-04', time: '09:00' }
  const splitAppt = (str) => {
    const [day, time = ''] = String(str || '').split(' ');
    return { day, time: time.slice(0, 5) };
  };

  // Al hacer clic en un resultado, el calendario salta a la vista del día de esa
  // cita (sin cerrar los resultados, por si la persona quiere revisar varias).
  const goToApptDay = (apptStr) => {
    const { day } = splitAppt(apptStr);
    const [y, m, d] = day.split('-').map(Number);
    if (!y || !m || !d) return;
    setYear(y); setMonth(m - 1); setDayNum(d); setView('day');
  };

  // El socket se crea una sola vez (depende solo del token); este ref siempre
  // apunta al refetch actual para no reconectar al navegar entre vistas/fechas.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // Tiempo real: cuando una cita cambia en el backend (la agenda el bot, la web
  // pública, un pago, u otra pestaña), el servidor emite 'appointment_changed' a
  // la empresa y refrescamos la vista actual sin que el admin tenga que recargar.
  useEffect(() => {
    if (!token) return;
    const socket = io(`${SOCKET_BASE}/admin`, { auth: { token } });
    socket.on('appointment_changed', () => refetchRef.current());
    return () => socket.disconnect();
  }, [token]);

  // Month navigation
  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1);
  };

  // Day navigation
  const prevDay = () => {
    const d = new Date(year, month, dayNum - 1);
    setYear(d.getFullYear()); setMonth(d.getMonth()); setDayNum(d.getDate());
  };
  const nextDay = () => {
    const d = new Date(year, month, dayNum + 1);
    setYear(d.getFullYear()); setMonth(d.getMonth()); setDayNum(d.getDate());
  };

  const openDay = (d) => { setDayNum(d); setView('day'); };

  // Número listo para wa.me: solo dígitos, con indicativo del país de la empresa.
  // Si el celular ya trae el indicativo no lo duplica; si no lo tiene, lo antepone.
  const waNumber = (phone) => {
    let digits = String(phone || '').replace(/\D/g, '');
    if (!digits) return null;
    if (dialCode && !digits.startsWith(dialCode)) digits = dialCode + digits;
    // Caso especial Argentina (54): WhatsApp exige un "9" tras el indicativo para
    // celulares. Si ya empieza por 549 se respeta; si trae 54 sin el 9, se inserta.
    if (dialCode === '54' && digits.startsWith('54') && !digits.startsWith('549')) {
      digits = '549' + digits.slice(2);
    }
    return digits;
  };

  // Status quick-update
  const updateStatus = async (id, status) => {
    await fetch(`/api/appointments/${id}`, {
      method: 'PUT', headers: jsonH, body: JSON.stringify({ status }),
    });
    refetch();
  };

  // Edit modal
  const openEdit = (appt) => {
    const dt = new Date(appt.appointment.replace(' ', 'T'));
    setEditForm({
      date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
      time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
      duration: String(appt.duration),
      name: appt.name, phone: cleanPhone(appt.phone), service: appt.service, status: appt.status,
    });
    setEditError(''); setEditAppt(appt);
  };

  const saveEdit = async () => {
    setSavingEdit(true); setEditError('');
    const r = await fetch(`/api/appointments/${editAppt.id}`, {
      method: 'PUT', headers: jsonH,
      body: JSON.stringify({
        appointment: `${editForm.date} ${editForm.time}:00`,
        duration: parseInt(editForm.duration),
        name: editForm.name, phone: editForm.phone,
        service: editForm.service, status: editForm.status,
      }),
    });
    if (r.ok) { setEditAppt(null); refetch(); }
    else { const j = await r.json(); setEditError(j.message || 'Error al guardar'); }
    setSavingEdit(false);
  };

  // Create modal
  const openCreate = () => {
    const dateStr = view === 'day'
      ? toDateStr(year, month, dayNum)
      : toDateStr(today.getFullYear(), today.getMonth(), today.getDate());
    const onlyOne = bookableStaff.length === 1 ? String(bookableStaff[0].id) : '';
    setCreateForm({
      date: dateStr, time: '09:00', name: '', phone: '', service_id: '', duration: '',
      // Se precarga el trabajador del filtro "Ver citas de:" si hay uno puesto,
      // pero sigue siendo un campo visible y obligatorio.
      assigned_user_id: staffFilter ? String(staffFilter) : onlyOne,
    });
    setCreateError(''); setShowCreate(true);
  };

  const onServiceChange = (id) => {
    const svc = services.find(s => s.id === id);
    setCreateForm(f => ({ ...f, service_id: id, duration: svc ? String(svc.time) : f.duration }));
  };

  const saveCreate = async () => {
    if (!createForm.name.trim() || !createForm.phone.trim() || !createForm.service_id || !createForm.duration) {
      return setCreateError('Todos los campos son requeridos');
    }
    // Con calendario por usuario la cita SIEMPRE debe tener dueño: una cita sin
    // asignar no entra en la agenda de nadie y no bloquea la hora en el
    // agendamiento público (se ofrecía una hora ya ocupada).
    if (perUserCalendar && isAdmin && !createForm.assigned_user_id) {
      return setCreateError('Elige el trabajador que atiende la cita');
    }
    setSavingCreate(true); setCreateError('');
    const svc = services.find(s => s.id === createForm.service_id);
    const r = await fetch('/api/appointments', {
      method: 'POST', headers: jsonH,
      body: JSON.stringify({
        appointment: `${createForm.date} ${createForm.time}:00`,
        duration: parseInt(createForm.duration),
        phone: createForm.phone.trim(), name: createForm.name.trim(),
        service: svc ? svc.name : createForm.service_id,
        // El trabajador sale del campo del formulario. El supervisor (rol 3) no
        // elige: el backend le asigna la cita a sí mismo.
        assigned_user_id: (perUserCalendar && isAdmin && createForm.assigned_user_id)
          ? Number(createForm.assigned_user_id) : undefined,
      }),
    });
    if (r.ok) { setShowCreate(false); refetch(); }
    else { const j = await r.json(); setCreateError(j.message || 'Error'); }
    setSavingCreate(false);
  };

  // Abre el cajero en el modo elegido, prellenado con los datos del cliente y el
  // servicio de la cita. Al crear el documento allí, la cita queda ligada (tabla
  // intermedia) y este botón pasará a ser "Imprimir".
  const startFacturar = async (appt, action) => {
    setFacturarAppt(null);
    setPreparing(appt.id);
    let items = [];
    try {
      const r = await fetch(`/api/appointments/${appt.id}/invoice-items`, { headers: authH });
      const json = r.ok ? await r.json() : { data: [] };
      items = json.data || [];
    } catch { /* si falla, se abre igual con los datos del cliente */ }
    setPreparing(null);
    navigate('/cajero', {
      state: {
        cajeroPrefill: {
          action, appointmentId: appt.id,
          customer: { name: appt.name || '', phone: appt.phone || '' },
          items,
        },
      },
    });
  };

  // Imprime el documento del cajero ligado a la cita (una vez facturada).
  const printInvoice = async (appt) => {
    setPreparing(appt.id);
    try {
      const r = await fetch(`/api/cajero/docs/${appt.invoice_doc_type}/${appt.invoice_doc_id}/print`, { headers: authH });
      const data = await r.json();
      if (r.ok && data.url) window.open(data.url, '_blank');
    } catch { /* noop */ }
    setPreparing(null);
  };

  // Group appointments by day (for month view)
  const appointmentsByDay = useMemo(() => {
    const map = {};
    appointments.forEach(a => {
      const k = a.appointment.slice(0, 10);
      if (!map[k]) map[k] = [];
      map[k].push(a);
    });
    return map;
  }, [appointments]);

  // Group appointments by hour (for day view)
  const appointmentsByHour = useMemo(() => {
    const map = {};
    appointments.forEach(a => {
      const h = parseInt((a.appointment.split(' ')[1] || '00:00').split(':')[0]);
      if (!map[h]) map[h] = [];
      map[h].push(a);
    });
    return map;
  }, [appointments]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7; // 0=Mon
  const todayStr = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());

  const ArrowBtn = ({ onClick, label }) => (
    <button onClick={onClick} style={{
      background: 'none', border: `1px solid ${theme.colors.border}`,
      color: theme.colors.text, borderRadius: '7px',
      padding: '6px 16px', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1,
    }}>{label}</button>
  );

  // ───── MONTH VIEW ─────
  const renderMonth = () => {
    const cells = [];
    for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
      <div>
        {/* Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem' }}>
          <ArrowBtn onClick={prevMonth} label="←" />
          <span style={{ color: theme.colors.primary, fontWeight: 700, fontSize: '1.3rem', letterSpacing: '2px' }}>
            {toMonthStr(year, month)}
          </span>
          <ArrowBtn onClick={nextMonth} label="→" />
        </div>

        {/* Day-of-week headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: isMobile ? '2px' : '4px', marginBottom: '4px' }}>
          {DAY_HEADERS.map(h => (
            <div key={h} style={{ textAlign: 'center', color: theme.colors.textMuted, fontSize: '0.72rem', fontWeight: 600, padding: '4px 0' }}>{h}</div>
          ))}
        </div>

        {/* Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: isMobile ? '2px' : '4px' }}>
          {cells.map((d, i) => {
            if (d === null) return <div key={`e${i}`} />;
            const dateStr = toDateStr(year, month, d);
            const dayAppts = appointmentsByDay[dateStr] || [];
            const isHoliday = holidays.has(dateStr);
            const isToday = dateStr === todayStr;
            const hasPending = dayAppts.some(a => a.status === 'pendiente');

            let bg = 'transparent';
            if (isHoliday) bg = 'rgba(251,191,36,0.28)';
            else if (hasPending) bg = 'rgba(34,197,94,0.18)';
            else if (dayAppts.length > 0) bg = 'rgba(107,114,128,0.18)';

            return (
              <div
                key={d}
                onClick={() => openDay(d)}
                title={isHoliday ? 'Festivo' : ''}
                style={{
                  backgroundColor: bg,
                  borderRadius: '8px',
                  border: isToday
                    ? `2px solid ${theme.colors.primary}`
                    : `1px solid ${theme.colors.border}22`,
                  padding: isMobile ? '6px 2px 5px' : '8px 4px 6px',
                  minHeight: isMobile ? '46px' : '54px',
                  cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                  transition: 'opacity 0.12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.72'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                <span style={{
                  fontSize: '0.85rem',
                  fontWeight: isToday ? 700 : 400,
                  color: isToday ? theme.colors.primary : isHoliday ? '#d97706' : theme.colors.text,
                }}>{d}</span>
                {dayAppts.length > 0 && (
                  <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {dayAppts.slice(0, 4).map(a => (
                      <div key={a.id} style={{
                        width: '5px', height: '5px', borderRadius: '50%',
                        backgroundColor:
                          a.status === 'pendiente' ? '#22c55e'
                          : a.status === 'atendida' ? '#6b7280'
                          : '#ef4444',
                      }} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {loading && <p style={{ color: theme.colors.textMuted, textAlign: 'center', marginTop: '1rem', fontSize: '0.82rem' }}>Cargando...</p>}

        {/* Legend */}
        <div style={{ display: 'flex', gap: '1.2rem', marginTop: '1.2rem', flexWrap: 'wrap' }}>
          {[
            { color: 'rgba(251,191,36,0.4)', label: 'Festivo' },
            { color: 'rgba(34,197,94,0.3)', label: 'Cita pendiente' },
            { color: 'rgba(107,114,128,0.3)', label: 'Cita atendida/cancelada' },
          ].map(({ color, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: color }} />
              <span style={{ color: theme.colors.textMuted, fontSize: '0.75rem' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ───── DAY VIEW ─────
  const renderDay = () => {
    const dateStr = toDateStr(year, month, dayNum);
    return (
      <div>
        {/* Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button onClick={() => setView('month')} style={{
              background: 'none', border: `1px solid ${theme.colors.border}`,
              color: theme.colors.textMuted, borderRadius: '7px',
              padding: '6px 12px', cursor: 'pointer', fontSize: '0.8rem',
            }}>← Mes</button>
            <ArrowBtn onClick={prevDay} label="←" />
          </div>

          <span style={{ color: theme.colors.primary, fontWeight: 700, fontSize: '1.3rem', letterSpacing: '2px' }}>
            {dateStr}
          </span>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <ArrowBtn onClick={nextDay} label="→" />
            <button onClick={openCreate} style={{
              backgroundColor: theme.colors.primary, color: '#000', border: 'none',
              padding: '6px 14px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem',
            }}>+ Nueva cita</button>
          </div>
        </div>

        {loading && <p style={{ color: theme.colors.textMuted, fontSize: '0.82rem', marginBottom: '0.5rem' }}>Cargando...</p>}

        {/* Hour slots */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {HOURS.map(h => {
            const slotAppts = appointmentsByHour[h] || [];
            return (
              <div key={h} style={{ display: 'flex', gap: '12px', minHeight: slotAppts.length > 0 ? 'auto' : '38px', alignItems: 'flex-start' }}>
                {/* Hour label */}
                <div style={{
                  width: '48px', flexShrink: 0, color: theme.colors.textMuted,
                  fontSize: '0.75rem', paddingTop: '9px', textAlign: 'right',
                }}>
                  {pad(h)}:00
                </div>
                {/* Slot content */}
                <div style={{ flex: 1, borderTop: `1px solid ${theme.colors.border}22`, paddingTop: '5px', paddingBottom: slotAppts.length > 0 ? '8px' : '0' }}>
                  {slotAppts.map(appt => {
                    const timePart = appt.appointment.split(' ')[1]?.slice(0, 5) || '';
                    const sc = STATUS_C[appt.status] || STATUS_C.pendiente;
                    return (
                      <div key={appt.id} style={{
                        backgroundColor: sc.bg,
                        border: `1px solid ${sc.border}`,
                        borderRadius: '8px', padding: '8px 12px', marginBottom: '6px',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '6px' }}>
                          <div>
                            <span style={{ color: theme.colors.text, fontWeight: 600, fontSize: '0.88rem' }}>{appt.name}</span>
                            <span style={{ color: theme.colors.textMuted, fontSize: '0.76rem', marginLeft: '8px' }}>{timePart} · {appt.duration} min</span>
                            <div style={{ color: theme.colors.textMuted, fontSize: '0.76rem', marginTop: '2px' }}>
                              {appt.service} · {appt.phone}
                            </div>
                          </div>
                          <span style={{
                            color: sc.text, fontSize: '0.72rem', fontWeight: 600,
                            textTransform: 'capitalize', backgroundColor: `${sc.border}22`,
                            padding: '2px 8px', borderRadius: '4px',
                          }}>{appt.status}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                          {waNumber(appt.phone) && (
                            <a
                              href={`https://wa.me/${waNumber(appt.phone)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: '5px',
                                backgroundColor: '#25D366', color: '#fff', border: 'none',
                                padding: '3px 10px', borderRadius: '5px', cursor: 'pointer',
                                fontSize: '0.72rem', fontWeight: 600, textDecoration: 'none',
                              }}
                            >
                              <svg viewBox="0 0 24 24" width="13" height="13" fill="#fff" aria-hidden="true">
                                <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.66.15-.2.3-.76.96-.93 1.16-.17.2-.34.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.13.3-.34.45-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.66-1.6-.9-2.18-.24-.58-.48-.5-.66-.5h-.56c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.24-.69.24-1.28.17-1.41-.07-.13-.27-.2-.57-.35z M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2z"/>
                              </svg>
                              Contactar
                            </a>
                          )}
                          {appt.status !== 'atendida' && (
                            <button onClick={() => updateStatus(appt.id, 'atendida')} style={{ background: 'none', border: '1px solid #6b7280', color: '#9ca3af', padding: '3px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.72rem' }}>Atendida</button>
                          )}
                          {appt.status !== 'cancelada' && (
                            <button onClick={() => updateStatus(appt.id, 'cancelada')} style={{ background: 'none', border: '1px solid #ef4444', color: '#ef4444', padding: '3px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.72rem' }}>Cancelar</button>
                          )}
                          {appt.status !== 'pendiente' && (
                            <button onClick={() => updateStatus(appt.id, 'pendiente')} style={{ background: 'none', border: '1px solid #22c55e', color: '#22c55e', padding: '3px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.72rem' }}>Pendiente</button>
                          )}
                          <button onClick={() => openEdit(appt)} style={{ background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, padding: '3px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.72rem' }}>Editar</button>
                          {appt.invoice_doc_type ? (
                            <button
                              onClick={() => printInvoice(appt)}
                              disabled={preparing === appt.id}
                              title={`Imprimir ${appt.invoice_full_number || ''}`}
                              style={{ background: theme.colors.primary, border: 'none', color: '#000', padding: '3px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, opacity: preparing === appt.id ? 0.6 : 1 }}
                            >
                              {preparing === appt.id ? '…' : '🖨 Imprimir'}
                            </button>
                          ) : (
                            <button
                              onClick={() => setFacturarAppt(appt)}
                              disabled={preparing === appt.id}
                              style={{ background: 'none', border: `1px solid ${theme.colors.primary}`, color: theme.colors.primary, padding: '3px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, opacity: preparing === appt.id ? 0.6 : 1 }}
                            >
                              {preparing === appt.id ? '…' : 'Facturar'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Modal common styles
  const overlay = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '1rem' : 0, overflowY: 'auto' };
  const box = { backgroundColor: theme.colors.surface, borderRadius: '12px', padding: isMobile ? '1.2rem' : '2rem', width: '100%', maxWidth: '430px', border: `1px solid ${theme.colors.border}`, boxSizing: 'border-box', maxHeight: isMobile ? '90vh' : 'none', overflowY: isMobile ? 'auto' : 'visible' };
  const modalGrid2 = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.8rem', marginBottom: '0.8rem' };

  return (
    <div style={{ padding: isMobile ? '1.2rem 0.6rem' : '2rem', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '0.8rem', flexWrap: 'wrap' }}>
        <h2 style={{ color: theme.colors.primary, margin: 0 }}>Citas</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          {perUserCalendar && (roleId === 1 || roleId === 2) && staff.length > 0 && (
            <select
              value={staffFilter}
              onChange={e => setStaffFilter(e.target.value)}
              title="Ver citas de"
              style={{ ...inputSt, width: 'auto', minWidth: '160px', cursor: 'pointer' }}
            >
              <option value="">Todas las agendas</option>
              {staff.map(u => (
                <option key={u.id} value={String(u.id)}>{u.name}</option>
              ))}
            </select>
          )}
          {isAdmin && (
            <button onClick={openExport} style={{
              backgroundColor: 'transparent', color: theme.colors.text,
              border: `1px solid ${theme.colors.border}`,
              padding: '7px 18px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem',
            }}>Exportar citas</button>
          )}
          {view === 'month' && (
            <button onClick={openCreate} style={{
              backgroundColor: theme.colors.primary, color: '#000', border: 'none',
              padding: '7px 18px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem',
            }}>+ Nueva cita</button>
          )}
        </div>
      </div>

      {/* ── Buscador de citas ── */}
      <div style={{
        border: `1px solid ${theme.colors.border}`,
        borderRadius: '10px',
        padding: isMobile ? '0.9rem' : '1rem 1.2rem',
        marginBottom: '1.5rem',
        backgroundColor: theme.colors.surface,
      }}>
        <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: isMobile ? '1 1 100%' : '1 1 180px', minWidth: 0 }}>
            <label style={labelSt}>Nombre</label>
            <input
              style={inputSt}
              value={searchName}
              onChange={e => setSearchName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
              placeholder="Ej. María"
            />
          </div>
          <div style={{ flex: isMobile ? '1 1 100%' : '1 1 180px', minWidth: 0 }}>
            <label style={labelSt}>Celular</label>
            <input
              style={inputSt}
              value={searchPhone}
              onChange={e => setSearchPhone(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
              placeholder="Ej. 3001234567"
            />
          </div>
          <button
            onClick={doSearch}
            disabled={searching || (!searchName.trim() && !searchPhone.trim())}
            style={{
              backgroundColor: theme.colors.primary, color: '#000', border: 'none',
              padding: '8px 20px', borderRadius: '7px',
              cursor: searching || (!searchName.trim() && !searchPhone.trim()) ? 'not-allowed' : 'pointer',
              opacity: searching || (!searchName.trim() && !searchPhone.trim()) ? 0.6 : 1,
              fontWeight: 600, fontSize: '0.88rem', flex: isMobile ? '1 1 100%' : '0 0 auto',
            }}
          >
            {searching ? 'Buscando...' : 'Buscar'}
          </button>
        </div>

        {/* Resultados informativos: el calendario no se mueve */}
        {searchResults !== null && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem', gap: '0.6rem' }}>
              <span style={{ color: theme.colors.textMuted, fontSize: '0.82rem' }}>
                {searchResults.length === 0
                  ? 'No se encontraron citas con esos datos.'
                  : searchResults.length === 10
                    ? 'Mostrando las 10 citas más recientes (de la más reciente a la más antigua):'
                    : `${searchResults.length} cita${searchResults.length === 1 ? '' : 's'} encontrada${searchResults.length === 1 ? '' : 's'} (de la más reciente a la más antigua):`}
              </span>
              <button
                onClick={clearSearch}
                title="Cerrar resultados"
                style={{
                  background: 'none', border: `1px solid ${theme.colors.border}`,
                  color: theme.colors.text, borderRadius: '6px',
                  width: '28px', height: '28px', flexShrink: 0, cursor: 'pointer',
                  fontSize: '1rem', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>

            {searchResults.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '340px', overflowY: 'auto' }}>
                {searchResults.map(a => {
                  const { day, time } = splitAppt(a.appointment);
                  const sc = STATUS_C[a.status] || STATUS_C.pendiente;
                  return (
                    <div
                      key={a.id}
                      onClick={() => goToApptDay(a.appointment)}
                      title="Ver este día en el calendario"
                      style={{
                        border: `1px solid ${sc.border}`,
                        backgroundColor: sc.bg,
                        borderRadius: '8px',
                        padding: '10px 12px',
                        fontSize: '0.84rem',
                        color: theme.colors.text,
                        cursor: 'pointer',
                        transition: 'opacity 0.12s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '0.78'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    >
                      <div style={{ lineHeight: 1.5 }}>
                        <strong>{a.name}</strong>
                        {' '}(<span>{a.phone}</span>){' '}tiene una cita para el día{' '}
                        <strong>{day}</strong> a la hora <strong>{time}</strong>.
                      </div>
                      <div style={{ color: theme.colors.textMuted, fontSize: '0.78rem', marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                        <span>Servicio: {a.service || '—'}</span>
                        {a.duration != null && <span>Duración: {a.duration} min</span>}
                        <span style={{ color: sc.text, textTransform: 'capitalize' }}>Estado: {a.status}</span>
                        <span>Trabajador: {a.assigned_user_name || 'Sin asignar'}</span>
                        <span style={{ color: theme.colors.primary }}>› Abrir día</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {view === 'month' ? renderMonth() : renderDay()}

      {/* ── Edit Modal ── */}
      {editAppt && (
        <div style={overlay}>
          <div style={box}>
            <h3 style={{ color: theme.colors.primary, margin: '0 0 1.2rem' }}>Editar cita</h3>
            <div style={modalGrid2}>
              <div>
                <label style={labelSt}>Fecha</label>
                <input type="date" style={inputSt} value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              <div>
                <label style={labelSt}>Hora</label>
                <input type="time" style={inputSt} value={editForm.time} onChange={e => setEditForm(f => ({ ...f, time: e.target.value }))} />
              </div>
            </div>
            <div style={fieldSt}>
              <label style={labelSt}>Nombre del cliente</label>
              <input style={inputSt} value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div style={fieldSt}>
              <label style={labelSt}>Teléfono</label>
              <input style={inputSt} value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: cleanPhone(e.target.value) }))} inputMode={strictPhone ? 'numeric' : 'tel'} />
            </div>
            <div style={fieldSt}>
              <label style={labelSt}>Servicio</label>
              <input style={inputSt} value={editForm.service} onChange={e => setEditForm(f => ({ ...f, service: e.target.value }))} />
            </div>
            <div style={modalGrid2}>
              <div>
                <label style={labelSt}>Duración (min)</label>
                <input type="number" onWheel={e => e.currentTarget.blur()} style={inputSt} value={editForm.duration} min="1" onChange={e => setEditForm(f => ({ ...f, duration: e.target.value }))} />
              </div>
              <div>
                <label style={labelSt}>Estado</label>
                <select style={{ ...inputSt, cursor: 'pointer' }} value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="pendiente">Pendiente</option>
                  <option value="atendida">Atendida</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>
            </div>
            {editError && <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: '0 0 0.8rem' }}>{editError}</p>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditAppt(null)} style={{ background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, padding: '7px 16px', borderRadius: '7px', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={saveEdit} disabled={savingEdit} style={{ backgroundColor: theme.colors.primary, color: '#000', border: 'none', padding: '7px 18px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600 }}>
                {savingEdit ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Export Modal ── */}
      {showExport && (
        <div style={overlay}>
          <div style={box}>
            <h3 style={{ color: theme.colors.primary, margin: '0 0 0.4rem' }}>Exportar citas</h3>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.84rem', margin: '0 0 1.2rem' }}>
              Selecciona el rango de fechas que deseas exportar. Se descargará un archivo CSV con toda la información de las citas.
            </p>
            <div style={modalGrid2}>
              <div>
                <label style={labelSt}>Desde</label>
                <input type="date" style={inputSt} value={exportForm.from} onChange={e => setExportForm(f => ({ ...f, from: e.target.value }))} />
              </div>
              <div>
                <label style={labelSt}>Hasta</label>
                <input type="date" style={inputSt} value={exportForm.to} onChange={e => setExportForm(f => ({ ...f, to: e.target.value }))} />
              </div>
            </div>
            {exportError && <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: '0 0 0.8rem' }}>{exportError}</p>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowExport(false)} style={{ background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, padding: '7px 16px', borderRadius: '7px', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={doExport} disabled={exporting} style={{ backgroundColor: theme.colors.primary, color: '#000', border: 'none', padding: '7px 18px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600 }}>
                {exporting ? 'Exportando...' : 'Exportar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Modal ── */}
      {showCreate && (
        <div style={overlay}>
          <div style={box}>
            <h3 style={{ color: theme.colors.primary, margin: '0 0 1.2rem' }}>Nueva cita</h3>
            <div style={modalGrid2}>
              <div>
                <label style={labelSt}>Fecha</label>
                <input type="date" style={inputSt} value={createForm.date} onChange={e => setCreateForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              <div>
                <label style={labelSt}>Hora</label>
                <input type="time" style={inputSt} value={createForm.time} onChange={e => setCreateForm(f => ({ ...f, time: e.target.value }))} />
              </div>
            </div>
            {perUserCalendar && isAdmin && (
              <div style={fieldSt}>
                <label style={labelSt}>Trabajador que atiende *</label>
                {bookableStaff.length === 0 ? (
                  <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: 0 }}>
                    No hay trabajadores habilitados para recibir citas. Habilítalos en "Configuración horario".
                  </p>
                ) : (
                  <select
                    style={{ ...inputSt, cursor: 'pointer' }}
                    value={createForm.assigned_user_id}
                    onChange={e => setCreateForm(f => ({ ...f, assigned_user_id: e.target.value }))}
                  >
                    <option value="">— Seleccionar trabajador —</option>
                    {bookableStaff.map(u => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
                  </select>
                )}
              </div>
            )}
            <div style={fieldSt}>
              <label style={labelSt}>Nombre del cliente *</label>
              <input style={inputSt} value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej. María García" />
            </div>
            <div style={fieldSt}>
              <label style={labelSt}>Teléfono *</label>
              <input style={inputSt} value={createForm.phone} onChange={e => setCreateForm(f => ({ ...f, phone: cleanPhone(e.target.value) }))} placeholder="Ej. 3001234567" inputMode={strictPhone ? 'numeric' : 'tel'} />
            </div>
            <div style={fieldSt}>
              <label style={labelSt}>Servicio *</label>
              <select style={{ ...inputSt, cursor: 'pointer' }} value={createForm.service_id} onChange={e => onServiceChange(e.target.value)}>
                <option value="">— Seleccionar servicio —</option>
                {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.time} min)</option>)}
              </select>
            </div>
            <div style={fieldSt}>
              <label style={labelSt}>Duración (min) *</label>
              <input type="number" onWheel={e => e.currentTarget.blur()} style={inputSt} value={createForm.duration} min="1" onChange={e => setCreateForm(f => ({ ...f, duration: e.target.value }))} placeholder="Se llena al elegir servicio" />
            </div>
            {createError && <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: '0 0 0.8rem' }}>{createError}</p>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, padding: '7px 16px', borderRadius: '7px', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={saveCreate} disabled={savingCreate} style={{ backgroundColor: theme.colors.primary, color: '#000', border: 'none', padding: '7px 18px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600 }}>
                {savingCreate ? 'Creando...' : 'Crear cita'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: elegir tipo de documento al facturar una cita ── */}
      {facturarAppt && (
        <div style={overlay} onClick={() => setFacturarAppt(null)}>
          <div style={box} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
              <h3 style={{ color: theme.colors.primary, margin: 0 }}>Facturar cita</h3>
              <button onClick={() => setFacturarAppt(null)} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.84rem', margin: '0 0 1.2rem' }}>
              ¿Qué documento quieres generar para <strong style={{ color: theme.colors.text }}>{facturarAppt.name || facturarAppt.phone}</strong>? Se abrirá el cajero con los datos del cliente y el servicio ya cargados.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {FACTURAR_OPTS.map(opt => (
                <button key={opt.action} onClick={() => startFacturar(facturarAppt, opt.action)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'left',
                    background: theme.colors.background, border: `1px solid ${theme.colors.border}`,
                    color: theme.colors.text, padding: '12px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = theme.colors.primary; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = theme.colors.border; }}
                >
                  <span style={{ fontSize: '1.3rem' }}>{opt.icon}</span>{opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Citas;
